import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Fab from '@mui/material/Fab';
import CloseIcon from '@mui/icons-material/Close';
import { setDialogOpen, setSelectedEvent } from '../../store/Actions';
import {Divider} from '../common/Divider';
import {MyAvatar} from '../common/MyAvatar';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import { RichTextEditor } from '../common/richtexteditor/RichTextEditor';
import { getSelectedEventPanelWidth } from './mapLayout';

const selectedEventPanelBaseStyle = {
    backgroundColor:'white',
    marginRight:'20px',
    borderRadius:'20px',
    boxShadow: '0px 0px 7px 0px grey',
    overflowY:'scroll',
    overflowX:'hidden',
    boxSizing:'border-box',
    position:'relative'
}

const panelContainerStyle = {
    width:'100%',
    display:'flex',
    flexDirection:'column',
    padding:'20px',
    paddingTop:'10px',
    boxSizing:'border-box',
    minWidth:0
}

const fabRowStyle = {
    position:'sticky',
    top:'12px',
    zIndex:3,
    height:0,
    display:'flex',
    justifyContent:'flex-end',
    gap:'10px',
    paddingRight:'15px',
    pointerEvents:'none'
}

const fabStyle = {
    pointerEvents:'auto'
}

const coverPicStyle = {
    height:'310px', 
    width:'100%',
    objectFit:'cover'
}

const titleStyle = {
    fontSize:'30px',
    fontWeight:400,
    width:'100%',
    overflowWrap:'anywhere'
}

const locationStyle = {
    fontSize:'16px',
    fontWeight:400,
    marginTop:'10px',
    display:'flex',
    flexDirection:'row',
    alignItems:'flex-start',
    paddingRight:'20px',
    minWidth:0,
    overflowWrap:'anywhere',
    gap:'4px'
}

const dateStyle = {
    fontSize:'15px',
    fontWeight:400,
    marginTop:'10px',
    display:'flex',
    flexDirection:'row',
    flexWrap:'wrap',
    minWidth:0,
    overflowWrap:'anywhere'
}

const friendsStyle = {
    fontSize:'15px',
    fontWeight:400,
    marginTop:'10px',
    display:'flex',
    flexDirection:'column',
    width:'100%',
    minWidth:0,
}

const descriptionStyle = {
    fontSize:'15px',
    fontWeight:400,
    display:'flex',
    flexDirection:'column',
    width:'100%',
    minWidth:0,
    overflowWrap:'anywhere'
}

const avatarNameStyle = {
    fontSize:'11px',
    fontWeight:600,
    maxWidth:'70px',
    overflowWrap:'normal',
    wordBreak:'normal',
    textAlign:'center'
}

const getDateString = (date, time) => {
    const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const timeOptions = {hour: '2-digit', minute: '2-digit'};
    date = date instanceof Date ? date : date.toDate();
    let strDate = date.toLocaleDateString("en-US", dateOptions);
    if(time){
        time = time instanceof Date ? time : time.toDate();
        strDate += ' ' + time.toLocaleTimeString([],timeOptions);
    }
    return strDate;
}

export function SelectedEventPanel() {
    const appWidth = useSelector(state=>state.appWidth);
    const viewingUserAccount = useSelector(state=>state.viewingUserAccount);
    const loggedInUserAccount = useSelector(state=>state.loggedInUserAccount);
    const viewingLoggedInUser = viewingUserAccount && loggedInUserAccount && viewingUserAccount.id === loggedInUserAccount.id;
    const userCategories = useSelector(state=>state.viewingUserCategories);
    const userTrips = useSelector(state=>state.viewingUserTrips);
    const userFriends = useSelector(state=>state.viewingUserFriends);
    const userTags = useSelector(state=>state.viewingUserTags);
    const event = useSelector(state=>state.selectedEvent);
    const placesService = useSelector(state=>state.placesService);
    const sideNavOpen = useSelector(state=>state.sideNavOpen);
    const embed = useSelector(state=>state.embed);
    const [placePhotoUrl, setPlacePhotoUrl] = useState('');
    const dispatch = useDispatch();

    const selectedEventPanelStyle = {
        ...selectedEventPanelBaseStyle,
        width: getSelectedEventPanelWidth(appWidth, sideNavOpen) + 'px',
        height: '100%',
        marginRight: appWidth <= 560 ? '12px' : selectedEventPanelBaseStyle.marginRight
    }
    const eventCoverPicStyle = {
        ...coverPicStyle,
        height: (appWidth <= 560 || embed) ? '155px' : coverPicStyle.height
    }
    if(!event)
        return null;

    if(!event.coverPicUrl){
        if(placesService){
            placesService.getDetails({placeId: event.location.placeId}, (result, status) => {
                if(result.photos)
                    setPlacePhotoUrl(result.photos[1].getUrl())
            })
        }
    }

    return (
        <div style={selectedEventPanelStyle}>
            <div style={fabRowStyle}>
                {viewingLoggedInUser &&
                    <Fab 
                        size="small"
                        style={fabStyle}
                        onClick={()=>{dispatch(setSelectedEvent(null));dispatch(setDialogOpen('Edit Event', {event}))}}
                    >
                        <EditIcon/>
                    </Fab>
                }
                <Fab 
                    size="small"
                    style={fabStyle}
                    onClick={()=>dispatch(setSelectedEvent(null))}
                >
                    <CloseIcon/>
                </Fab>
            </div>
            {(event.coverPicUrl || placePhotoUrl) &&
                <img src={event.coverPicUrl ? event.coverPicUrl : placePhotoUrl} alt="Event cover" style={eventCoverPicStyle} />
            }
            {!(event.coverPicUrl || placePhotoUrl) &&
                <div style={{height:'55px', backgroundColor:userCategories[event.categoryId].pinColor, display:'flex', flexDirection:'row', justifyContent:'center', alignItems:'center'}}>
                    <div style={{fontSize:'40px'}}>{event.emoji}</div>
                </div>
            }
            <div style={panelContainerStyle}>
                <div style={titleStyle}>{event.title}</div>
                <div style={locationStyle}>
                    <LocationOnIcon style={{flexShrink:0}}/>
                    <span style={{minWidth:0}}>{event.location.description}</span>
                </div>
                <Divider style={{marginLeft:'-20px', marginTop:'15px'}}/>
                <div style={dateStyle}>
                    <div style={{fontWeight:600, marginRight:'5px'}}>{event.endDate ? 'Started:' : 'Date:'} </div>
                    {getDateString(event.startDate, event.startTime)}
                </div>
                {event.endDate && 
                    <div style={dateStyle}>
                        <div style={{fontWeight:600, marginRight:'5px'}}>Ended: </div>
                        {getDateString(event.endDate, event.endTime)}
                    </div>
                }
                <div style={dateStyle}>
                    <div style={{fontWeight:600, marginRight:'5px'}}>Category: </div>
                    {userCategories[event.categoryId].name}
                </div>
                <div style={friendsStyle}>
                    <div style={{display:'flex', flexDirection:'row', justifyContent:'start', flexWrap:'wrap', marginTop:'5px'}}>
                        <div style={{display:'flex', flexDirection:'column', alignItems:'center', marginRight:'10px'}}>
                            <MyAvatar userAccount={viewingUserAccount} style={{height:'70px', width:'70px'}}/>
                            <div style={avatarNameStyle}>{viewingUserAccount.displayName}</div>
                        </div>
                        {event.friendIds.map(friendId=>{
                            const friend = userFriends[friendId];
                            return (
                                <div style={{display:'flex', flexDirection:'column', alignItems:'center', marginRight:'10px'}}>
                                    <MyAvatar userAccount={friend} style={{height:'70px', width:'70px'}}/>
                                    <div style={avatarNameStyle}>{friend.name}</div>
                                </div>
                            );

                        })}
                    </div>
                </div>
                {event.tripIds.length > 0 && 
                    <div style={friendsStyle}>
                        <div style={{fontWeight:600, marginRight:'5px'}}>Trips: </div>
                        <div style={{display:'flex', flexDirection:'row', justifyContent:'start', flexWrap:'wrap', gap:'8px', marginTop:'5px'}}>
                            {event.tripIds.map(tripId=>{
                                const trip = userTrips[tripId];
                                return (
                                    <div style={{display:'flex', flexDirection:'column', alignItems:'center', minWidth:0}}>
                                        <div>{trip.name}</div>
                                    </div>
                                );
                                
                            })}
                        </div>
                    </div>
                }
                {event.tagIds.length > 0 && 
                    <div style={friendsStyle}>
                        <div style={{fontWeight:600, marginRight:'5px'}}>Tags: </div>
                        <div style={{display:'flex', flexDirection:'row', justifyContent:'start', flexWrap:'wrap', gap:'8px', marginTop:'5px'}}>
                            {event.tagIds.map(tagId=>{
                                const tag = userTags[tagId];
                                return (
                                    <div style={{display:'flex', flexDirection:'column', alignItems:'center', minWidth:0}}>
                                        <div>{tag.name}</div>
                                    </div>
                                );
                                
                            })}
                        </div>
                    </div>
                }
                <Divider style={{marginLeft:'-20px', marginTop:'20px'}}/>
                <div style={descriptionStyle} key={event.id}>
                    <RichTextEditor readonly={true} contentHTML={event.descriptionHTML}/>
                </div>
            </div>
        </div>
    );
}
  
